// Las dos vistas de UNA evidencia en la pestaña: tarjeta (cuadrícula, para VER
// fotos) y fila compacta (lista, ~50px: caben 13 archivos donde antes 5).
// Comparten props, acciones y el chip de compresión; por eso viven juntas.
// Cero tooltips: el nombre completo y la fecha con hora van a la vista, y qué
// hace cada botón lo explica la Ayuda de Evidencias.
// Vivían dentro de TabEvidencias.tsx (1,174 líneas); se sacaron íntegras el 2026-08-17.
import type { EvidenciaOut, ImeiOut } from "@/lib/api";
import { leyendaEliminacion } from "@/lib/retencion";
import { fmtFechaCargaLarga } from "@/lib/fechas";
import { fmtBytes } from "@/lib/formato";
import EstadoCompresion, { enCursoCompresion, sinArchivoCompresion } from "./EstadoCompresion";
import { ThumbArchivo, iconoArchivo, tieneThumb } from "./thumbs";

const fmtSize = fmtBytes;

export function EvidenciaCard({
  ev,
  imeis,
  puedeEditar,
  onAsignar,
  onEliminar,
  onDescartar,
  onReintentar,
  onCompartir,
  onVer,
}: {
  ev: EvidenciaOut;
  imeis: ImeiOut[];
  puedeEditar: boolean;
  onAsignar: (imeiId: number | null) => void;
  onEliminar: () => void;
  onDescartar: () => void;
  onReintentar: () => void;
  onCompartir: () => void;
  onVer: () => void;
}) {
  const isImage = ev.mime.startsWith("image/");
  const isVideo = ev.mime.startsWith("video/");
  const sizeStr = fmtSize(ev.size_bytes);
  const eliminado = ev.archivo_eliminado_at !== null;
  const enCurso = enCursoCompresion(ev);
  const sinArchivo = sinArchivoCompresion(ev);
  const servible = !eliminado && !enCurso && !sinArchivo;
  const retencion = eliminado || enCurso || sinArchivo ? null : leyendaEliminacion(ev.expira_at);

  return (
    <div className="rounded-lg border border-ink-700 bg-ink-900 overflow-hidden">
      {eliminado ? (
        <div className="aspect-video bg-ink-800 flex flex-col items-center justify-center gap-1 text-center px-2">
          <span className="text-2xl opacity-40">🗑️</span>
          <span className="text-[10px] text-zinc-500">
            Archivo eliminado por retención
          </span>
        </div>
      ) : enCurso ? (
        <div className="aspect-video bg-ink-800 flex flex-col items-center justify-center gap-2 text-[11px] text-zinc-500">
          <span className="w-7 h-7 rounded-full border-[3px] border-accent-400/30 border-t-accent-400 animate-spin" />
          Comprimiendo video…
        </div>
      ) : sinArchivo ? (
        <div className="aspect-video bg-ink-800 flex items-center justify-center text-3xl opacity-40">
          {ev.estado_compresion === "error" ? "⚠️" : "📦"}
        </div>
      ) : (
      <button
        type="button"
        onClick={onVer}
        aria-label={`Ver ${ev.nombre_original}`}
        className="block w-full aspect-video bg-ink-800 flex items-center justify-center overflow-hidden cursor-zoom-in"
      >
        {isImage ? (
          <img
            src={`/api/evidencias/${ev.id}/file`}
            alt={ev.nombre_original}
            loading="lazy"
            className="object-cover w-full h-full"
          />
        ) : tieneThumb(ev.mime, ev.nombre_original) ? (
          <ThumbArchivo
            ev={ev}
            className={`object-cover w-full h-full ${isVideo ? "" : "object-top"}`}
            fallbackClassName="text-3xl"
          />
        ) : (
          <span className="text-3xl">{iconoArchivo(ev.mime, ev.nombre_original)}</span>
        )}
      </button>
      )}
      <div className="p-2 space-y-1">
        <p className="text-xs font-medium text-zinc-200 break-words [overflow-wrap:anywhere]">
          {ev.nombre_original}
        </p>
        <p className="text-[10px] text-zinc-500">
          {sizeStr} · {ev.mime.split("/")[1] || ev.mime}
          {ev.subido_por_nombre && ` · ${ev.subido_por_nombre}`}
          {" · "}
          subido el {fmtFechaCargaLarga(ev.created_at)}
        </p>
        {eliminado ? (
          <p className="text-[10px] text-red-400/70">
            Archivo eliminado por retención · se conserva el registro
          </p>
        ) : (
          retencion && (
            <p
              className={`text-[10px] ${
                retencion.urgente ? "text-amber-400" : "text-zinc-600"
              }`}
            >
              {retencion.texto}
            </p>
          )
        )}
        {ev.estado_compresion && (
          <EstadoCompresion ev={ev} onReintentar={onReintentar} onDescartar={onDescartar} />
        )}
        {puedeEditar && !sinArchivo && (
          <div className="flex items-center gap-1 pt-1">
            <select
              className="input text-[11px] py-0.5 flex-1"
              value={ev.imei_id ?? ""}
              onChange={(e) => onAsignar(e.target.value ? parseInt(e.target.value) : null)}
              aria-label="Asociar a un IMEI"
            >
              <option value="">— sin IMEI —</option>
              {imeis.map((i) => (
                <option key={i.id} value={i.id}>
                  IMEI {i.imei}
                </option>
              ))}
            </select>
            {servible && (
              <button
                onClick={onCompartir}
                aria-label="Copiar enlace público"
                className="text-zinc-500 hover:text-accent-400 text-xs px-1"
              >
                🔗
              </button>
            )}
            <button
              onClick={onEliminar}
              aria-label="Eliminar archivo"
              className="text-zinc-500 hover:text-red-400 text-xs px-2"
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Fila compacta de la vista lista: mini-thumb, nombre, meta y acciones en
// ~50px de alto — 13 archivos caben en una pantalla en vez de 5 filas de
// tarjetas. La cuadrícula sigue disponible para VER las fotos.
export function EvidenciaFila({
  ev,
  imeis,
  puedeEditar,
  onAsignar,
  onEliminar,
  onDescartar,
  onReintentar,
  onCompartir,
  onVer,
}: {
  ev: EvidenciaOut;
  imeis: ImeiOut[];
  puedeEditar: boolean;
  onAsignar: (imeiId: number | null) => void;
  onEliminar: () => void;
  onDescartar: () => void;
  onReintentar: () => void;
  onCompartir: () => void;
  onVer: () => void;
}) {
  const isImage = ev.mime.startsWith("image/");
  const isVideo = ev.mime.startsWith("video/");
  const sizeStr = fmtSize(ev.size_bytes);
  const eliminado = ev.archivo_eliminado_at !== null;
  const enCurso = enCursoCompresion(ev);
  const sinArchivo = sinArchivoCompresion(ev);
  const retencion = eliminado || enCurso || sinArchivo ? null : leyendaEliminacion(ev.expira_at);
  const noVerible = eliminado || enCurso || sinArchivo;
  const servible = !noVerible;

  return (
    <li className="flex items-center gap-2 py-1.5">
      <button
        type="button"
        onClick={onVer}
        disabled={noVerible}
        aria-label={`Ver ${ev.nombre_original}`}
        className="w-10 h-10 rounded bg-ink-800 overflow-hidden flex items-center justify-center shrink-0 cursor-zoom-in disabled:cursor-default"
      >
        {eliminado ? (
          <span className="text-base opacity-40">🗑️</span>
        ) : enCurso ? (
          <span className="w-4 h-4 rounded-full border-2 border-accent-400/30 border-t-accent-400 animate-spin" />
        ) : sinArchivo ? (
          <span className="text-base opacity-40">
            {ev.estado_compresion === "error" ? "⚠️" : "📦"}
          </span>
        ) : isImage ? (
          <img
            src={`/api/evidencias/${ev.id}/file`}
            alt={ev.nombre_original}
            loading="lazy"
            className="object-cover w-full h-full"
          />
        ) : tieneThumb(ev.mime, ev.nombre_original) ? (
          <ThumbArchivo
            ev={ev}
            className={`object-cover w-full h-full ${isVideo ? "" : "object-top"}`}
            fallbackClassName="text-base"
          />
        ) : (
          <span className="text-base">{iconoArchivo(ev.mime, ev.nombre_original)}</span>
        )}
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-zinc-200 break-words [overflow-wrap:anywhere]">
          {ev.nombre_original}
        </p>
        <p className="text-[10px] text-zinc-500">
          {sizeStr} · {ev.mime.split("/")[1] || ev.mime}
          {ev.subido_por_nombre && ` · ${ev.subido_por_nombre}`}
          {" · "}
          subido el {fmtFechaCargaLarga(ev.created_at)}
          {eliminado ? (
            <span className="text-red-400/70"> · eliminado por retención</span>
          ) : (
            retencion && (
              <span className={retencion.urgente ? "text-amber-400" : ""}>
                {" "}
                · {retencion.texto}
              </span>
            )
          )}
        </p>
        {ev.estado_compresion && (
          <div className="mt-1">
            <EstadoCompresion ev={ev} onReintentar={onReintentar} onDescartar={onDescartar} />
          </div>
        )}
      </div>
      {puedeEditar && !sinArchivo && (
        <>
          <select
            className="input text-[11px] py-0.5 w-[120px] sm:w-[170px] shrink-0"
            value={ev.imei_id ?? ""}
            onChange={(e) => onAsignar(e.target.value ? parseInt(e.target.value) : null)}
            aria-label="Asociar a un IMEI"
          >
            <option value="">— sin IMEI —</option>
            {imeis.map((i) => (
              <option key={i.id} value={i.id}>
                IMEI {i.imei}
              </option>
            ))}
          </select>
          {servible && (
            <button
              onClick={onCompartir}
              aria-label="Copiar enlace público"
              className="text-zinc-500 hover:text-accent-400 text-xs px-1 shrink-0"
            >
              🔗
            </button>
          )}
          <button
            onClick={onEliminar}
            aria-label="Eliminar archivo"
            className="text-zinc-500 hover:text-red-400 text-xs px-1 shrink-0"
          >
            ×
          </button>
        </>
      )}
    </li>
  );
}
